import useUiContext from "@/custolink-hooks/useUiContext";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  PublicAssetFrontendUserApi,
  PublicAssetFrontendUserApi$SigninValidateEmail,
  PublicAssetFrontendUserApi$SigninWithPassword,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import useFormServerBacked from "@/custolink-hooks/form/useFormServerBacked";
import useFormErrorPicker from "@/custolink-hooks/form/useFormErrorPicker";
import { Controller } from "react-hook-form";
import toast from "@/components/ui/toast";
import Notification from "@/components/ui/Notification";
import { rdaNotify } from "@/custolink-views/asset_frontend/redesigns/RdaToast";
import PasswordInput from "../../../components/shared/PasswordInput";
import { PublicSharedUserApi } from "@/custolink-transpiled_by_server/bc.identity_and_access.api_methods";
import { useTranslation } from "react-i18next";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import {
  FormField,
  PasswordField,
  PrimaryButton,
  SecondaryButton,
  TextField,
} from "@/custolink-views/asset_frontend/redesigns/formKit";

// Shared with the classic design (AssetViewClassic/AssetFrontendHome's AccountDrawer renders
// this same component), so the success toast and the password field follow the
// asset-frontend switch; the rest of this file (TextField, the buttons) was already
// redesigned unconditionally by an earlier batch.
function signedInToast(newDesign: boolean, message: string) {
  if (newDesign) {
    rdaNotify.success(message);
  } else {
    toast.push(<Notification title={message} type="success" />, { placement: "top-center" });
  }
}

export default function SignInForm(p: { back: () => void }) {
  const uic = useUiContext();
  const [email, setEmail] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [mode, setMode] = useState<
    "present_email" | "options" | "signin_with_code" | "forgot_password"
  >("present_email");

  // signinWithCodeRequest
  const genCodeMutation = useMutation({
    mutationFn: () =>
      PublicAssetFrontendUserApi.signinWithCodeRequest(
        uic.organisationContext.organisationId,
        email as string,
      ),
    mutationKey: ["signinWithCodeRequest", uic._orgId],
  });

  useEffect(() => {
    if (mode == "signin_with_code" && !codeSent) {
      try {
        genCodeMutation.mutate();
      } finally {
        setCodeSent(true);
      }
    }
  }, [mode, codeSent]);

  if (mode == "present_email") {
    return (
      <PresentEmailSubform
        continue={(email) => {
          setEmail(email);
          setMode("options");
        }}
        back={p.back}
      />
    );
  } else if (email != null && mode == "options") {
    return (
      <OptionsSubform
        email={email}
        back={p.back}
        continueToSignInWithCode={() => {
          setMode("signin_with_code");
        }}
        forgotPassword={()=>{
          setMode("forgot_password")
        }}
      />
    );
  } else if (email != null && mode == "signin_with_code") {
    return <SigninWithCode email={email} back={p.back} />;
  } else if(email != null &&mode == "forgot_password"){
    return <ForgotPasswordPart email={email} back={p.back} />
  }
  return null;
}

function PresentEmailSubform(p: {
  continue: (email: string) => void;
  back: () => void;
}) {
  const uic = useUiContext();
  const {t} = useTranslation()
  const form =
    useFormServerBacked<PublicAssetFrontendUserApi$SigninValidateEmail>(
      {
        email: "",
      },
      (d) => {
        return PublicAssetFrontendUserApi.signinValidateEmail(
          uic.organisationContext.organisationId,
          {
            email: d.email,
          },
        );
      },
      (a) => {
        p.continue(a.email);
      },
      undefined,
      "asset",
    );

  const e = useFormErrorPicker(form.errors, "email");

  return (
    <form onSubmit={form.handleSubmit()}>
      <div className="space-y-4">
        <FormField label={t("account.signIn.email")} error={e.email}>
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <TextField
                type="email"
                autoComplete="off"
                placeholder={t("account.signIn.email")}
                invalid={Boolean(e.email)}
                {...field}
              />
            )}
          />
        </FormField>

        <PrimaryButton type="submit" disabled={form.isSubmitting}>
          {t('account.signIn.continue')}
        </PrimaryButton>

        <SecondaryButton className="w-full" type="button" onClick={p.back}>
          {t("account.signIn.back")}
        </SecondaryButton>
      </div>
    </form>
  );
}

function OptionsSubform(p: {
  email: string;
  continueToSignInWithCode: () => void;
  forgotPassword: () => void;
  back: () => void;
}) {
  const uic = useUiContext();
  const {t} = useTranslation()
  const newDesign = useAssetFrontendDesign();
  const form = useFormServerBacked<
    PublicAssetFrontendUserApi$SigninWithPassword>(
    {
      email: p.email,
    },
    (d) => {
      return PublicAssetFrontendUserApi.signinWithPassword(
        uic.organisationContext.organisationId,
        {
          email: d.email,
          password: d.password,
        },
      );
    },
    () => {
      signedInToast(newDesign, t('account.signIn.success.signedOnNow'));
      setTimeout(() => {
        document.location.reload();
      }, 250);
    },
    undefined,
    "asset",
  );

  const e = useFormErrorPicker(form.errors, "password","email");

  return (
    <form onSubmit={form.handleSubmit()}>
      <div className="space-y-4">
        <p className="text-sm text-[var(--rda-muted)]">
          {t('account.signIn.pleaseEnterPasswordToSignIn')}
        </p>

        <FormField label={t('account.signIn.password')} error={e.password ?? e.email}>
          <Controller
            name="password"
            control={form.control}
            rules={{ required: true }}
            render={({ field }) =>
              newDesign ? (
                <PasswordField
                  placeholder={t('account.signIn.password')}
                  autoComplete="off"
                  {...field}
                />
              ) : (
                <PasswordInput
                  type="text"
                  placeholder={t('account.signIn.password')}
                  autoComplete="off"
                  {...field}
                />
              )
            }
          />
        </FormField>

        <div>
          <a
            className="text-sm font-semibold text-[var(--rda-accent-text)] underline"
            href="#"
            onClick={(ev) => {
              ev.preventDefault()
              p.forgotPassword()
            }}
          >
            {t('account.signIn.forgotPassword')}
          </a>
        </div>

        <PrimaryButton type="submit" disabled={!form.isDirty || form.isSubmitting}>
          {t('account.signIn.signIn')}
        </PrimaryButton>

        <p className="text-sm text-[var(--rda-muted)]">
          {t('account.signIn.orGetSignInCodeSentToYourEmail')}
        </p>

        <SecondaryButton
          className="w-full"
          type="button"
          disabled={form.isSubmitting}
          onClick={() => p.continueToSignInWithCode()}
        >
          {t('account.signIn.signInWithCode')}
        </SecondaryButton>

        <SecondaryButton className="w-full" type="button" onClick={p.back}>
          {t('account.signIn.back')}
        </SecondaryButton>
      </div>
    </form>
  );
}

function ForgotPasswordPart(p: { email: string; back: () => void }) {
  const uic = useUiContext();
  const {t} = useTranslation()
  useQuery({
    queryFn: () =>
      PublicSharedUserApi.forgotPassword({
        organisationId: uic._orgId,
        email: p.email,
        originAdminArea: false,
      }),
    queryKey: ["PublicSharedUserApi_forgotPassword", uic._orgId,p.email,false],
    enabled: true
  });

  return <p className="text-sm text-[var(--rda-muted)]">
    {t("account.signIn.reset.emailHasBeenSent")}
  </p>
}

function SigninWithCode(p: { email: string; back: () => void }) {
  const uic = useUiContext();
  const ref = useRef<HTMLInputElement>(null);
  const {t} = useTranslation()
  const newDesign = useAssetFrontendDesign();

  const [code, setCode] = useState("");
  const [codeCheckFailed, setCodeCheckFailed] = useState<undefined | string>(
    undefined,
  );
  const m = useMutation({
    mutationFn: (code: string) =>
      PublicAssetFrontendUserApi.signinWithCodeCheck(
        uic.organisationContext.organisationId,
        {
          email: p.email,
          code: code,
        },
      ),
    mutationKey: ["signinWithCodeRequest", uic._orgId],
  });

  const check = async (code: string) => {
    setCodeCheckFailed(undefined);
    try {
      const r = await m.mutateAsync(code);
      if (r.success) {
        signedInToast(newDesign, t('account.signIn.success.signedOnNow'));
        setTimeout(() => {
          document.location.reload();
        }, 250);
      } else {
        setCodeCheckFailed(r.errorMessage);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (ref.current) ref.current.focus();
  }, []);

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        check(code);
      }}
    >
      <div className="space-y-4">
        <p className="text-sm text-[var(--rda-muted)]">
          {t("account.signIn.code.pleaseCheckEmailAndEnterCode")}
        </p>

        <FormField error={codeCheckFailed}>
          <TextField
            ref={ref}
            type="text"
            autoComplete="off"
            invalid={codeCheckFailed != undefined}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
          />
        </FormField>

        <PrimaryButton type="submit" disabled={m.isPending}>
          {t("account.signIn.signIn")}
        </PrimaryButton>

        <SecondaryButton className="w-full" type="button" onClick={p.back}>
          {t("account.signIn.back")}
        </SecondaryButton>
      </div>
    </form>
  );
}
